import React, { useState, useEffect, useRef } from 'react';
import Card from '../../components/card/Card';
import Button from '../../components/button/Button';
import Input from '../../components/input/Input';
import P from '../../components/texts/p/P';
import H from '../../components/texts/h/H';
import { useClientTheme } from '../../context/ThemeContext';
import {
  CURRENCY_CODES,
  APPLICATION_STATUS,
  MONTH_NAMES,
} from '../../utils/Constants';
import { isCommitteeStatus } from '../../utils/applicationStatusUtils';
import DropDown from '../../components/dropdown/DropDown';
import { useFormik } from 'formik';
import {
  saveBalanceItems,
  updateBalanceItems,
} from '../../services/RiskService';
import { Toast, useToast } from '../../components/toast/toast';
import Spinner from '../../components/Spinner/spinner';
import Span from '../../components/texts/span/Span';
import { balanceInfo } from '../../types/interfaces';
import Modal from '../../components/modal/modal';
import TextArea from '../../components/textarea/TextArea';
import { updateApplication } from '../../services/ApplicationService';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  hasData,
  hasError,
  isEmptyValue,
  isSuccessfulResponse,
  toNumber,
  computeGrossResultTotal,
  computeBalanceSheetPayloadTotals,
  computeResultForTheYear,
  GrossResultInput,
} from '../../utils/Utils';
import {
  navigateAfterFlowAction,
  type DetailLocationState,
} from '../../utils/listNavigationState';
import { NumericFormat } from 'react-number-format';
import { useRiskPdf } from '../../hooks/useRiskPdf';
import useRole from '../../hooks/useRole';

interface DualBalanceLoaderProps {
  cuit: string | undefined;
  applicationId: string | undefined;
  balanceInfo: balanceInfo | undefined;
  handleNextStep: Function;
  handlePurchasesSalesData: Function;
  setBalanceInfo: Function;
  setLocalBalanceId: Function;
  localBalanceId: number;
  applicationStatus: number | string;
  previousBalanceInfo?: balanceInfo | undefined;
  setPreviousBalanceInfo?: Function;
  setPreviousLocalBalanceId?: Function;
  previousLocalBalanceId?: number;
  categoryType?: { id: number; name: string } | null;
  onBalanceSaved?: (code: string) => void;
}

const initialBalanceData: balanceInfo = {
  id: 0,
  from_month: 0,
  from_year: 0,
  to_month: 0,
  to_year: 0,
  current_assets: {
    current_investments: '',
    cash_and_cash_equivalents: '',
    trade_receivables_short_term: '',
    other_receivables: '',
    inventory_short_term: '',
    other_current_assets: '',
  },
  non_current_assets: {
    trade_receivables_long_term: '',
    other_receivables: '',
    permanent_investments: '',
    property_plant_and_equipment: '',
    other_non_current_assets: '',
  },
  current_liabilities: {
    trade_payables_short_term: '',
    bank_and_financial_debt_short_term: '',
    social_security_debt_short_term: '',
    tax_liabilities_short_term: '',
    other_debts_provisions: '',
    projections: '',
    other_liabilities: '',
  },
  non_current_liabilities: {
    non_current_liabilities: '',
    trade_payables_long_term: '',
    bank_and_financial_debt_long_term: '',
    social_security_debt_long_term: '',
    tax_liabilities_long_term: '',
    other_long_term_liabilities: '',
  },
  non_controlling_interest_result: {
    non_controlling_interest_result: '',
  },
  net_worth: {
    capital: '',
    legal_reserves: '',
    other_reserves: '',
    capital_adjustment: '',
    irrevocable_contributions: '',
    technical_revaluation: '',
    prior_accumulated: '',
    result_of_period: '',
    other: '',
  },
  gross_result: {
    agricultural_production: '',
    production_cost: '',
    sales: '',
    cost_of_sales: '',
  },
  operating_result: {
    commercialization_expenses: '',
    administrative_expenses: '',
    other_income: '',
    other_expenses: '',
  },
  ordinary_result: {
    financial_expenses: '',
    exchange_difference_and_holding_result: '',
  },
  result_before_taxes: {
    extraordinary_income: '',
    extraordinary_expenses: '',
  },
  taxes: {
    income_tax: '',
  },
  purchases: '',
  amortization_depreciation: '',
};

const initialTotals = {
  current_assets: '0,00',
  non_current_assets: '0,00',
  current_liabilities: '0,00',
  non_current_liabilities: '0,00',
  non_controlling_interest_result: '0,00',
  total_net_worth_and_non_current: '0,00',
  total_current: '0,00',
  total_non_current: '0,00',
  total_net_worth: '0,00',
  net_worth: '0,00',
  gross_result: '0,00',
  operating_result: '0,00',
  ordinary_result: '0,00',
  result_before_taxes: {
    extraordinary_income: '0,00',
    extraordinary_expenses: '0,00',
    total: 0,
    totalFormatted: '0,00',
  },
  result_before_taxes_calculated: 0,
  taxes: '0,00',
  income_tax_formatted: '0,00',
};

const DualBalanceLoader: React.FC<DualBalanceLoaderProps> = ({
  cuit,
  applicationId,
  balanceInfo: balanceInfoProp,
  handleNextStep,
  setBalanceInfo,
  setLocalBalanceId,
  localBalanceId,
  applicationStatus,
  previousBalanceInfo,
  setPreviousBalanceInfo,
  setPreviousLocalBalanceId,
  previousLocalBalanceId = 0,
  categoryType,
  onBalanceSaved,
}) => {
  const { clientTheme } = useClientTheme();
  const config = JSON.parse(localStorage.getItem('config') ?? '{}');
  const isUruguayEnabled = config?.isUruguayEnabled === true;
  const isResultadoBrutoFiltered = config?.isResultadoBrutoFilteredEnabled ?? false;
  const isComplementaryDataEnabled = config?.isComplementaryDataEnabled ?? true;
  const isDetailedNetWorth = config?.isDetailedNetWorthEnabled ?? false;
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state ?? {}) as DetailLocationState;
  const { canTransitionRisk } = useRole();

  const applyComplementaryDataDefaults = (data: balanceInfo): balanceInfo => {
    if (!isComplementaryDataEnabled) return data;

    return {
      ...data,
      purchases: isEmptyValue(data.purchases) ? 0 : data.purchases,
      amortization_depreciation: isEmptyValue(data.amortization_depreciation) ? 0 : data.amortization_depreciation,
    };
  };

  // Visualization mode state
  const [isEditableMode, setIsEditableMode] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isRejectApplication, setIsRejectApplication] = useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');
  
  // PDF export ref
  const balanceContainerRef = useRef<HTMLDivElement>(null);

  // Current balance state
  const [totals, setTotals] = useState(initialTotals);
  const [balanceData, setBalanceData] = useState<balanceInfo>(() =>
    applyComplementaryDataDefaults(balanceInfoProp || initialBalanceData)
  );
  
  // Previous balance state
  const [previousTotals, setPreviousTotals] = useState(initialTotals);
  const [previousBalanceData, setPreviousBalanceData] = useState<balanceInfo>(() =>
    applyComplementaryDataDefaults(previousBalanceInfo || initialBalanceData)
  );

  // Period selector state
  const [keys, setKeys] = useState<string[]>([]);
  const [values, setValues] = useState<string[]>([]);

  // Current balance period
  const [selectedKey, setSelectedKey] = useState<string | null>(
    balanceData.from_month > 0 && balanceData.from_year > 0
      ? `${MONTH_NAMES[balanceData.from_month - 1]}-${balanceData.from_year}`
      : null
  );
  const [correspondingValue, setCorrespondingValue] = useState<string | null>(
    balanceData.to_month > 0 && balanceData.to_year > 0
      ? `${MONTH_NAMES[balanceData.to_month - 1]}-${balanceData.to_year}`
      : null
  );

  // Previous balance period
  const [previousSelectedKey, setPreviousSelectedKey] = useState<string | null>(
    previousBalanceInfo && previousBalanceInfo.from_month > 0 && previousBalanceInfo.from_year > 0
      ? `${MONTH_NAMES[previousBalanceInfo.from_month - 1]}-${previousBalanceInfo.from_year}`
      : null
  );
  const [previousCorrespondingValue, setPreviousCorrespondingValue] = useState<string | null>(
    previousBalanceInfo && previousBalanceInfo.to_month > 0 && previousBalanceInfo.to_year > 0
      ? `${MONTH_NAMES[previousBalanceInfo.to_month - 1]}-${previousBalanceInfo.to_year}`
      : null
  );

  // PDF export hook
const { isGenerating: isPdfGenerating, generatePdfFromNode } = useRiskPdf({
  fileName: `balance_${cuit}_${selectedKey || 'current'}.pdf`,
  useImprovedPdfExport: true,
});

  // Formik instances
  const balanceFormik = useFormik({
    initialValues: balanceData,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  const previousBalanceFormik = useFormik({
    initialValues: previousBalanceData,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  const isApplicationEditable = () => {
    return !isCommitteeStatus(applicationStatus);
  };

  // Initialize period selector map
  useEffect(() => {
    const map = new Map();
    const startDate = new Date();
    startDate.setDate(1);
    startDate.setMonth(startDate.getMonth() - 12);

    const endDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    endDate.setDate(1);
    endDate.setMonth(endDate.getMonth() + 11);

    for (let i = 0; i <= 48; i++) {
      map.set(
        MONTH_NAMES[startDate.getMonth()] + '-' + startDate.getFullYear(),
        MONTH_NAMES[endDate.getMonth()] + '-' + endDate.getFullYear()
      );
      startDate.setMonth(startDate.getMonth() - 1);
      endDate.setMonth(endDate.getMonth() - 1);
    }

    setKeys(Array.from(map.keys()));
    setValues(Array.from(map.values()));
  }, []);

  // Función para migrar y normalizar net_worth
  const normalizeNetWorth = (netWorth: any) => {
    if (!netWorth) return initialBalanceData.net_worth;
    
    // Función helper para convertir valores a string, preservando 0 y números
    const toValueString = (value: any): string => {
      if (value === null || value === undefined || value === '') return '';
      // Si es número (incluyendo 0), convertirlo a string
      if (typeof value === 'number') {
        return String(value);
      }
      // Si ya es string, retornarlo tal cual
      if (typeof value === 'string') {
        return value;
      }
      return '';
    };
    
    if (!isDetailedNetWorth) {
      const toNum = (v: any): number => {
        if (v === null || v === undefined || v === '') return 0;
        if (typeof v === 'number') return v;
        const n = parseFloat(String(v));
        return isNaN(n) ? 0 : n;
      };

      const capitalCalc = toNum(netWorth.capital) + toNum(netWorth.capital_adjustment);
      const otherCalc = toNum(netWorth.legal_reserves) + toNum(netWorth.other_reserves) +
        toNum(netWorth.irrevocable_contributions) + toNum(netWorth.technical_revaluation) +
        toNum(netWorth.prior_accumulated) + toNum(netWorth.result_of_period) + toNum(netWorth.other);

      return {
        capital: String(Math.round(capitalCalc)),
        legal_reserves: '',
        other_reserves: '',
        capital_adjustment: '',
        irrevocable_contributions: '',
        technical_revaluation: '',
        prior_accumulated: '',
        result_of_period: '',
        other: String(Math.round(otherCalc)),
      };
    }

    const normalized = {
      capital: toValueString(netWorth.capital),
      legal_reserves: toValueString(netWorth.legal_reserves),
      other_reserves: toValueString(netWorth.other_reserves),
      capital_adjustment: toValueString(netWorth.capital_adjustment),
      irrevocable_contributions: toValueString(netWorth.irrevocable_contributions),
      technical_revaluation: toValueString(netWorth.technical_revaluation),
      prior_accumulated: toValueString(netWorth.prior_accumulated),
      result_of_period: toValueString(netWorth.result_of_period),
      other: toValueString(netWorth.other),
    };

    return normalized;
  };

  // Sincronizar balance actual solo cuando cambia el balance (id o período), no en cada re-render.
  // Si no, enableReinitialize resetea el form y se pierden los valores que el usuario escribió en
  // Gastos financieros y Dif. de cambio, y el cálculo siempre ve 0 ahí.
  const lastBalanceSyncedRef = useRef<{ id: number; from_year: number; from_month: number } | null>(null);
  useEffect(() => {
    if (!balanceInfoProp) return;
    const key = { id: balanceInfoProp.id, from_year: balanceInfoProp.from_year, from_month: balanceInfoProp.from_month };
    const prev = lastBalanceSyncedRef.current;
    if (prev && prev.id === key.id && prev.from_year === key.from_year && prev.from_month === key.from_month) {
      return;
    }
    lastBalanceSyncedRef.current = key;
    
    console.log('[DualBalanceLoader] balanceInfoProp recibido:', balanceInfoProp);
    console.log('[DualBalanceLoader] net_worth antes de normalizar:', balanceInfoProp.net_worth);
    
    const ord = balanceInfoProp.ordinary_result;
    const rbt = balanceInfoProp.result_before_taxes;
    const tax = balanceInfoProp.taxes;
    const normalizedNetWorth = normalizeNetWorth(balanceInfoProp.net_worth);
    
    console.log('[DualBalanceLoader] net_worth después de normalizar:', normalizedNetWorth);
    
    const updatedBalanceData = applyComplementaryDataDefaults({
      ...initialBalanceData,
      ...balanceInfoProp,
      net_worth: normalizedNetWorth,
      ordinary_result: typeof ord === 'object' && ord !== null
        ? { ...initialBalanceData.ordinary_result, ...ord }
        : initialBalanceData.ordinary_result,
      result_before_taxes: typeof rbt === 'object' && rbt !== null
        ? { ...initialBalanceData.result_before_taxes, ...rbt }
        : initialBalanceData.result_before_taxes,
      taxes: typeof tax === 'object' && tax !== null
        ? { ...initialBalanceData.taxes, ...tax }
        : initialBalanceData.taxes,
    });
    
    console.log('[DualBalanceLoader] balanceData actualizado:', updatedBalanceData);
    
    setBalanceData(updatedBalanceData);
  }, [balanceInfoProp]);

  // Sincronizar balance anterior solo cuando cambia (id o período), para no resetear el form y perder lo tipeado.
  const lastPreviousBalanceSyncedRef = useRef<{ id: number; from_year: number; from_month: number } | null>(null);
  useEffect(() => {
    if (!previousBalanceInfo) return;
    const key = { id: previousBalanceInfo.id, from_year: previousBalanceInfo.from_year, from_month: previousBalanceInfo.from_month };
    const prev = lastPreviousBalanceSyncedRef.current;
    if (prev && prev.id === key.id && prev.from_year === key.from_year && prev.from_month === key.from_month) {
      return;
    }
    lastPreviousBalanceSyncedRef.current = key;
    const ord = previousBalanceInfo.ordinary_result;
    const rbt = previousBalanceInfo.result_before_taxes;
    const tax = previousBalanceInfo.taxes;
    setPreviousBalanceData(applyComplementaryDataDefaults({
      ...initialBalanceData,
      ...previousBalanceInfo,
      net_worth: normalizeNetWorth(previousBalanceInfo.net_worth),
      ordinary_result: typeof ord === 'object' && ord !== null
        ? { ...initialBalanceData.ordinary_result, ...ord }
        : initialBalanceData.ordinary_result,
      result_before_taxes: typeof rbt === 'object' && rbt !== null
        ? { ...initialBalanceData.result_before_taxes, ...rbt }
        : initialBalanceData.result_before_taxes,
      taxes: typeof tax === 'object' && tax !== null
        ? { ...initialBalanceData.taxes, ...tax }
        : initialBalanceData.taxes,
    }));
    if (previousBalanceInfo.from_month > 0 && previousBalanceInfo.from_year > 0) {
      setPreviousSelectedKey(`${MONTH_NAMES[previousBalanceInfo.from_month - 1]}-${previousBalanceInfo.from_year}`);
    }
    if (previousBalanceInfo.to_month > 0 && previousBalanceInfo.to_year > 0) {
      setPreviousCorrespondingValue(`${MONTH_NAMES[previousBalanceInfo.to_month - 1]}-${previousBalanceInfo.to_year}`);
    }
  }, [previousBalanceInfo]);

  // Calculate current balance totals
  useEffect(() => {
    const v = balanceFormik.values;
    const ordRaw = v.ordinary_result;
    const financialExpensesNum = toNumber(ordRaw?.financial_expenses);
    const exchangeDiffNum = toNumber(ordRaw?.exchange_difference_and_holding_result);

    // Parseo formato europeo y redondeo a entero para totales (fix 49267)
    const sumValues = (obj: Record<string, unknown>) => {
      const sum = Object.values(obj).reduce<number>((sum, val) => sum + toNumber(val as string | number), 0);
      return Math.round(sum);
    };

    const formatEuro = (value: number) => {
      return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      }).format(value);
    };

    // Inputs del form (lo que se usa en las fórmulas)
    const gross_inputs = {
      agricultural_production: toNumber(v.gross_result?.agricultural_production),
      production_cost: toNumber(v.gross_result?.production_cost),
      sales: toNumber(v.gross_result?.sales),
      cost_of_sales: toNumber(v.gross_result?.cost_of_sales),
    };
    const operating_inputs = {
      commercialization_expenses: toNumber(v.operating_result?.commercialization_expenses),
      administrative_expenses: toNumber(v.operating_result?.administrative_expenses),
      other_income: toNumber(v.operating_result?.other_income),
      other_expenses: toNumber(v.operating_result?.other_expenses),
    };
    const ordinary_inputs = {
      financial_expenses: financialExpensesNum,
      exchange_difference_and_holding_result: exchangeDiffNum,
    };
    const rbt_inputs = {
      extraordinary_income: toNumber(v.result_before_taxes?.extraordinary_income),
      extraordinary_expenses: toNumber(v.result_before_taxes?.extraordinary_expenses),
    };
    const tax_input = toNumber(v.taxes?.income_tax);
    
    // El cálculo del Resultado Bruto siempre usa la fórmula completa, independientemente del flag
    // El flag solo afecta la visualización (ocultar campos)
    const gross_result = Math.round((
      gross_inputs.agricultural_production -
      gross_inputs.production_cost +
      gross_inputs.sales -
      gross_inputs.cost_of_sales
    ) * 100) / 100;

    // Resultado bruto - Gastos comercialización - Gastos administración + otros ingresos - otros egresos = Resultado operativo
    const operating_result = Math.round((
      gross_result -
      operating_inputs.commercialization_expenses -
      operating_inputs.administrative_expenses +
      operating_inputs.other_income -
      operating_inputs.other_expenses
    ) * 100) / 100;

    // Resultado operativo - gastos financieros +/- dif de cambio + resultado por tenencia = Resultado ordinario
    const ordinary_result = Math.round((
      operating_result -
      ordinary_inputs.financial_expenses +
      ordinary_inputs.exchange_difference_and_holding_result
    ) * 100) / 100;

    const result_before_taxes = Math.round((
      ordinary_result +
      rbt_inputs.extraordinary_income -
      rbt_inputs.extraordinary_expenses
    ) * 100) / 100;

    // Resultado del ejercicio = resultado antes de impuestos menos impuesto a las ganancias.
    const taxes = Math.round((result_before_taxes + tax_input) * 100) / 100;

    const total_current =
      toNumber(sumValues(balanceFormik.values.current_assets)) +
      toNumber(sumValues(balanceFormik.values.non_current_assets));
    const total_non_current =
      toNumber(sumValues(balanceFormik.values.current_liabilities)) +
      toNumber(sumValues(balanceFormik.values.non_current_liabilities));
    const total_net_worth = isDetailedNetWorth
      ? toNumber(sumValues(balanceFormik.values.net_worth))
      : toNumber(balanceFormik.values.net_worth.capital || 0) +
        toNumber(balanceFormik.values.net_worth.other || 0);
    const total_net_worth_and_non_current =
      toNumber(sumValues(balanceFormik.values.current_liabilities)) +
      toNumber(sumValues(balanceFormik.values.non_current_liabilities)) +
      total_net_worth +
      toNumber(sumValues(balanceFormik.values.non_controlling_interest_result));

    setTotals({
      current_assets: formatEuro(sumValues(balanceFormik.values.current_assets)),
      non_current_assets: formatEuro(sumValues(balanceFormik.values.non_current_assets)),
      current_liabilities: formatEuro(sumValues(balanceFormik.values.current_liabilities)),
      non_current_liabilities: formatEuro(sumValues(balanceFormik.values.non_current_liabilities)),
      non_controlling_interest_result: formatEuro(sumValues(balanceFormik.values.non_controlling_interest_result)),
      total_current: formatEuro(total_current),
      total_non_current: formatEuro(total_non_current),
      total_net_worth: formatEuro(total_net_worth),
      net_worth: formatEuro(total_net_worth),
      total_net_worth_and_non_current: formatEuro(total_net_worth_and_non_current),
      gross_result: formatEuro(gross_result),
      operating_result: formatEuro(operating_result),
      ordinary_result: formatEuro(ordinary_result),
      result_before_taxes: {
        extraordinary_income: formatEuro(toNumber(balanceFormik.values.result_before_taxes.extraordinary_income)),
        extraordinary_expenses: formatEuro(toNumber(balanceFormik.values.result_before_taxes.extraordinary_expenses)),
        total: result_before_taxes,
        totalFormatted: formatEuro(result_before_taxes),
      },
      result_before_taxes_calculated: result_before_taxes,
      taxes: formatEuro(taxes),
      income_tax_formatted: formatEuro(toNumber(balanceFormik.values.taxes.income_tax)),
    });
  }, [balanceFormik.values]);

  // Calculate previous balance totals (always from form values so manual period load still sums)
  useEffect(() => {
    const sumValues = (obj: Record<string, unknown> | undefined) => {
      const sum = Object.values(obj || {}).reduce<number>((sum, val) => sum + toNumber(val as string | number), 0);
      return Math.round(sum);
    };

    const formatEuro = (value: number) => {
      return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      }).format(value);
    };

    const prev = previousBalanceFormik.values;

    // El cálculo del Resultado Bruto siempre usa la fórmula completa, independientemente del flag
    // El flag solo afecta la visualización (ocultar campos)
    const gross_result = Math.round((
      toNumber(prev.gross_result?.agricultural_production) -
      toNumber(prev.gross_result?.production_cost) +
      toNumber(prev.gross_result?.sales) -
      toNumber(prev.gross_result?.cost_of_sales)
    ) * 100) / 100;

    // Resultado bruto - Gastos comercialización - Gastos administración + otros ingresos - otros egresos = Resultado operativo
    const operating_result = Math.round((
      gross_result -
      toNumber(prev.operating_result?.commercialization_expenses) -
      toNumber(prev.operating_result?.administrative_expenses) +
      toNumber(prev.operating_result?.other_income) -
      toNumber(prev.operating_result?.other_expenses)
    ) * 100) / 100;

    // Resultado operativo - gastos financieros +/- dif de cambio + resultado por tenencia = Resultado ordinario
    const ordinary_result = Math.round((
      operating_result -
      toNumber(prev.ordinary_result?.financial_expenses) +
      toNumber(prev.ordinary_result?.exchange_difference_and_holding_result)
    ) * 100) / 100;

    const result_before_taxes = Math.round((
      ordinary_result +
      toNumber(prev.result_before_taxes?.extraordinary_income) -
      toNumber(prev.result_before_taxes?.extraordinary_expenses)
    ) * 100) / 100;

    // Resultado del ejercicio = resultado antes de impuestos menos impuesto a las ganancias.
    // El impuesto se guarda negativo (gasto), por eso se implementa como suma del valor del campo.
    const taxes = Math.round((
      result_before_taxes + toNumber(prev.taxes?.income_tax)
    ) * 100) / 100;

    const total_current =
      toNumber(sumValues(prev.current_assets)) +
      toNumber(sumValues(prev.non_current_assets));
    const total_non_current =
      toNumber(sumValues(prev.current_liabilities)) +
      toNumber(sumValues(prev.non_current_liabilities));
    const total_net_worth = isDetailedNetWorth
      ? toNumber(sumValues(prev.net_worth))
      : toNumber(prev.net_worth?.capital || 0) +
        toNumber(prev.net_worth?.other || 0);
    const total_net_worth_and_non_current =
      toNumber(sumValues(prev.current_liabilities)) +
      toNumber(sumValues(prev.non_current_liabilities)) +
      total_net_worth +
      toNumber(sumValues(prev.non_controlling_interest_result));

    setPreviousTotals({
      current_assets: formatEuro(sumValues(prev.current_assets)),
      non_current_assets: formatEuro(sumValues(prev.non_current_assets)),
      current_liabilities: formatEuro(sumValues(prev.current_liabilities)),
      non_current_liabilities: formatEuro(sumValues(prev.non_current_liabilities)),
      non_controlling_interest_result: formatEuro(sumValues(prev.non_controlling_interest_result)),
      total_current: formatEuro(total_current),
      total_non_current: formatEuro(total_non_current),
      total_net_worth: formatEuro(total_net_worth),
      net_worth: formatEuro(total_net_worth),
      total_net_worth_and_non_current: formatEuro(total_net_worth_and_non_current),
      gross_result: formatEuro(gross_result),
      operating_result: formatEuro(operating_result),
      ordinary_result: formatEuro(ordinary_result),
      result_before_taxes: {
        extraordinary_income: formatEuro(toNumber(prev.result_before_taxes?.extraordinary_income)),
        extraordinary_expenses: formatEuro(toNumber(prev.result_before_taxes?.extraordinary_expenses)),
        total: result_before_taxes,
        totalFormatted: formatEuro(result_before_taxes),
      },
      result_before_taxes_calculated: result_before_taxes,
      taxes: formatEuro(taxes),
      income_tax_formatted: formatEuro(toNumber(prev.taxes?.income_tax)),
    });
  }, [previousBalanceFormik.values]);

  const replaceEmptyValues = (obj: Record<string, unknown>): Record<string, unknown> => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        if (key === 'result_before_taxes' && value && typeof value === 'object') {
          const resultValue = value as { extraordinary_income?: unknown; extraordinary_expenses?: unknown; total?: number };
          return [
            key,
            {
              extraordinary_income: isEmptyValue(resultValue.extraordinary_income) ? 0 : toNumber(resultValue.extraordinary_income as string),
              extraordinary_expenses: isEmptyValue(resultValue.extraordinary_expenses) ? 0 : toNumber(resultValue.extraordinary_expenses as string),
              total: resultValue.total
            }
          ];
        }
        return [
          key,
          value && typeof value === 'object'
            ? replaceEmptyValues(value as Record<string, unknown>)
            : isEmptyValue(value)
              ? 0
              : toNumber(value as string),
        ];
      })
    );
  };

  const saveData = (shouldContinue: boolean) => {
    const updatedFinancialData = replaceEmptyValues(balanceFormik.values as unknown as Record<string, unknown>);

    // Resultado bruto total (mismo cálculo que BalanceLoader) para que origination y EBIT lo usen
    const grossResultTotal = computeGrossResultTotal(updatedFinancialData.gross_result as GrossResultInput | undefined);
    const gr = updatedFinancialData.gross_result as Record<string, unknown> | undefined;
    updatedFinancialData.gross_result = {
      ...(gr || {}),
      total: grossResultTotal,
    };

    updatedFinancialData.result_before_taxes = {
      extraordinary_income: (updatedFinancialData.result_before_taxes as Record<string, unknown>).extraordinary_income,
      extraordinary_expenses: (updatedFinancialData.result_before_taxes as Record<string, unknown>).extraordinary_expenses,
      total: totals.result_before_taxes_calculated
    };

    updatedFinancialData.result_for_the_year = computeResultForTheYear(
      totals.result_before_taxes_calculated,
      (updatedFinancialData.taxes as Record<string, unknown> | undefined)?.income_tax
    );

    // Totales de activos/pasivos para que origination persista total_assets_amount (BPB_024, etc.)
    const payloadTotals = computeBalanceSheetPayloadTotals(updatedFinancialData);
    updatedFinancialData.current_assets = {
      ...(updatedFinancialData.current_assets as Record<string, unknown>),
      total: payloadTotals.currentAssetsTotal,
    };
    updatedFinancialData.non_current_assets = {
      ...(updatedFinancialData.non_current_assets as Record<string, unknown>),
      total: payloadTotals.nonCurrentAssetsTotal,
    };
    updatedFinancialData.total_assets_amount = payloadTotals.totalAssetsAmount;
    updatedFinancialData.total_liabilities_amount = payloadTotals.totalLiabilitiesAmount;

    setIsSaving(true);
    setIsDisabled(true);

    const doNextStep = () => {
      if (shouldContinue) {
        handleNextStep({
          values: updatedFinancialData,
          totals: totals,
        });
      }
    };

    const savePreviousBalanceThenNext = () => {
      if (!cuit || !applicationId) {
        doNextStep();
        setIsSaving(false);
        setIsDisabled(false);
        return;
      }
      const prevData = replaceEmptyValues(previousBalanceFormik.values as unknown as Record<string, unknown>);
      const prevGr = prevData.gross_result as Record<string, unknown> | undefined;
      const prevGrossResultTotal = computeGrossResultTotal(prevGr as GrossResultInput | undefined);
      prevData.gross_result = { ...(prevGr || {}), total: prevGrossResultTotal };
      prevData.result_before_taxes = {
        ...(prevData.result_before_taxes as Record<string, unknown>),
        total: previousTotals.result_before_taxes_calculated
      };
      prevData.result_for_the_year = computeResultForTheYear(
        previousTotals.result_before_taxes_calculated,
        (prevData.taxes as Record<string, unknown> | undefined)?.income_tax
      );
      const prevPayloadTotals = computeBalanceSheetPayloadTotals(prevData);
      prevData.current_assets = {
        ...(prevData.current_assets as Record<string, unknown>),
        total: prevPayloadTotals.currentAssetsTotal,
      };
      prevData.non_current_assets = {
        ...(prevData.non_current_assets as Record<string, unknown>),
        total: prevPayloadTotals.nonCurrentAssetsTotal,
      };
      prevData.total_assets_amount = prevPayloadTotals.totalAssetsAmount;
      prevData.total_liabilities_amount = prevPayloadTotals.totalLiabilitiesAmount;

      const hasPreviousPeriod =
        (prevData.from_month && Number(prevData.from_month) > 0) &&
        (prevData.from_year && Number(prevData.from_year) > 0);

      if (previousLocalBalanceId) {
        updateBalanceItems(cuit, applicationId, previousLocalBalanceId, prevData)
          .then((response) => {
            if (isSuccessfulResponse(response) && setPreviousBalanceInfo) {
              setPreviousBalanceInfo({ values: previousBalanceFormik.values, totals: previousTotals, code: previousBalanceInfo?.code });
            }
            doNextStep();
          })
          .catch(() => {
            useToast.error({
              title: 'Ocurrió un error al guardar el balance anterior.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });
            doNextStep();
          })
          .finally(() => {
            setIsSaving(false);
            setIsDisabled(false);
          });
        return;
      }
      if (!hasPreviousPeriod) {
        doNextStep();
        setIsSaving(false);
        setIsDisabled(false);
        return;
      }
      saveBalanceItems(cuit, applicationId, prevData)
        .then((response) => {
          if (hasData(response) && response.status === 201) {
            if (setPreviousLocalBalanceId) setPreviousLocalBalanceId(response.data.id);
            if (setPreviousBalanceInfo) setPreviousBalanceInfo({ values: previousBalanceFormik.values, totals: previousTotals, code: response.data.code });
            useToast.success({
              title: 'Se guardó el balance anterior correctamente.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });
          } else if (response.status === 409) {
            useToast.error({
              title: 'Ya existe un balance en el periodo del balance anterior.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });
          } else {
            useToast.error({
              title: 'Ocurrió un error al guardar el balance anterior.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });
          }
          doNextStep();
        })
        .catch(() => {
          useToast.error({
            title: 'Ocurrió un error al guardar el balance anterior.',
            id: 'balance-activity-toast',
            description: '',
            closeButton: false,
            autoClose: 5000,
          });
          doNextStep();
        })
        .finally(() => {
          setIsSaving(false);
          setIsDisabled(false);
        });
    };

    if (isApplicationEditable()) {
      if (localBalanceId === 0) {
        const hasCurrentPeriod =
          (updatedFinancialData.from_month && Number(updatedFinancialData.from_month) > 0) &&
          (updatedFinancialData.from_year && Number(updatedFinancialData.from_year) > 0);
        if (!hasCurrentPeriod) {
          useToast.error({
            title: 'Seleccioná el período del balance antes de guardar.',
            id: 'balance-activity-toast',
            description: '',
            closeButton: false,
            autoClose: 5000,
          });
          setIsSaving(false);
          setIsDisabled(false);
          return;
        }
        saveBalanceItems(
          cuit || '',
          applicationId || '',
          updatedFinancialData
        ).then((response) => {
          if (hasData(response) && response.status === 201) {
            setLocalBalanceId(response.data.id);
            if (onBalanceSaved && response.data.code) {
              onBalanceSaved(response.data.code);
            }
            useToast.success({
              title: 'Se guardó la información correctamente.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });

            setBalanceInfo({ values: updatedFinancialData, totals: totals });
              savePreviousBalanceThenNext();
          } else if (response.status === 409) {
            useToast.error({
              title: 'Ya existe un balance en el periodo solicitado.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });
            setIsSaving(false);
            setIsDisabled(false);
          } else {
            useToast.error({
              title: 'Ocurrió un error al guardar la información.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });
            setIsSaving(false);
            setIsDisabled(false);
          }
        });
      } else {
        updateBalanceItems(
          cuit || '',
          applicationId || '',
          localBalanceId,
          updatedFinancialData
        ).then((response) => {
          if (isSuccessfulResponse(response)) {
            useToast.success({
              title: 'Se guardó la información correctamente.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });

            setBalanceInfo({ values: updatedFinancialData, totals: totals });
            savePreviousBalanceThenNext();
          } else if (hasError(response).isClientError) {
            useToast.error({
              title: 'Ya existe un balance en el periodo solicitado.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });
            setIsSaving(false);
            setIsDisabled(false);
          } else {
            useToast.error({
              title: 'Ocurrió un error al guardar la información.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });
            setIsSaving(false);
            setIsDisabled(false);
          }
        });
      }
    } else {
      handleNextStep({ values: updatedFinancialData, totals: totals });
    }
  };

  const handleApplication = (status: string) => {
    setIsSaving(true);
    updateApplication(
      cuit || '',
      { status_code: status, comment: modalText },
      applicationId || ''
    ).then((response) => {
      const mapStatus = new Map();
      mapStatus.set(
        APPLICATION_STATUS.approved.label,
        'Aceptaste la documentación. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.rejected.label,
        'Declinaste la solicitud. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.pending.label,
        'Solicitaste cambios en la solicitud. El productor será notificado.'
      );

      if (response.status === 200) {
        useToast.success({
          title: mapStatus.get(status),
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigateAfterFlowAction(navigate, location.pathname, locationState);
        }, 1000);
      }
      setIsSaving(false);
    });
  };

  // Render a single input field for a column
  const renderColumnInput = (
    label: string,
    parentFieldName: string,
    childFieldName: string,
    formik: typeof balanceFormik,
    idPrefix: string,
    inputDisabled: boolean
  ) => {
    const allowNegative = 
      label === 'Impuestos a las ganancias' || 
      label === 'Capital' || 
      label === 'Revalúo Técnico' || 
      label === 'Ajuste de Capital' || 
      label === 'Acumuladas Ejercicio Anterior' || 
      label === 'Resultado del Ejercicio' || 
      label === 'Dif. de cambio + res. tenencia' ||
      label === 'Otros';
    const formikValues = formik.values as unknown as Record<string, Record<string, string | number>>;
    
    return (
      <Card display='flex' alignItems='center' gap='12px' margin={{ bottom: '2' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Span type='2' variant='neutral'>{label}</Span>
        </div>
        <div className="dual-balance-input">
          <NumericFormat
            disabled={inputDisabled || !isApplicationEditable() || !isEditableMode}
            id={`risk-balance-${idPrefix}-input-${parentFieldName}.${childFieldName}`}
            maxLength={40}
            placeholder='Ingresar monto'
            value={formikValues[parentFieldName]?.[childFieldName] ?? ''}
            thousandSeparator='.'
            decimalSeparator=','
            onValueChange={(formatValues) => {
              formik.setFieldValue(
                `${parentFieldName}.${childFieldName}`,
                formatValues.value === '' ? '' : formatValues.floatValue
              );
            }}
            customInput={Input}
            decimalScale={2}
            allowNegative={allowNegative}
          />
        </div>
      </Card>
    );
  };

  // Render section header for a single column
  const renderColumnSectionHeader = (title: string, total: string) => {
    return (
      <Card display='flex' alignItems='center' gap='12px' margin={{ bottom: '2' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <H type='4' variant='secondary-2' weight='bold'>{title}</H>
        </div>
        <div className="dual-balance-total dual-balance-value-col" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <H type='4' variant='success' weight='bolder'>
            {total}
          </H>
        </div>
      </Card>
    );
  };

  // Render total row for a single column. styleAsPromedio: mismo estilo que fila "Promedio" (primary, bold, sin fondo gris).
  const renderColumnTotalRow = (title: string, total: string, styleAsPromedio?: boolean) => {
    if (styleAsPromedio) {
      return (
        <div
          className="dual-balance-total-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '16px'
          }}
        >
          <div className="dual-balance-value-col dual-balance-total-row-title" style={{ padding: '14px 20px', display: 'flex', alignItems: 'center' }}>
            <P type='3' variant='primary' weight='bold' withMargin={false}>{title}</P>
          </div>
          <div className="dual-balance-value-col dual-balance-total-row-value" style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Span type='3' variant='primary' weight='bold'>
              {total}
            </Span>
          </div>
        </div>
      );
    }
    return (
      <div
        className="dual-balance-total-row"
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '16px',
          backgroundColor: '#E8E8E8',
          border: '1px solid #1a1a1a',
          borderRadius: '4px'
        }}
      >
        <div className="dual-balance-value-col dual-balance-total-row-title" style={{ padding: '14px 20px', display: 'flex', alignItems: 'center' }}>
          <H type='5' withMargin={false}>{title}</H>
        </div>
        <div className="dual-balance-value-col dual-balance-total-row-value" style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <H type='5' withMargin={false} weight='bolder'>
            {total}
          </H>
        </div>
      </div>
    );
  };

  // Render Estado Patrimonial section for a column (conceptos primero, total debajo en cada bloque)
  const renderEstadoPatrimonialColumn = (
    formik: typeof balanceFormik,
    columnTotals: typeof totals,
    idPrefix: string,
    inputDisabled: boolean
  ) => {
    return (
      <>
        {/* Activo corriente - conceptos luego total */}
        {renderColumnInput('Disponibilidades', 'current_assets', 'cash_and_cash_equivalents', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Inversiones corrientes PF', 'current_assets', 'current_investments', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Créditos por ventas CP', 'current_assets', 'trade_receivables_short_term', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Otros créditos', 'current_assets', 'other_receivables', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Bienes de cambio - CP', 'current_assets', 'inventory_short_term', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Otros activos corrientes', 'current_assets', 'other_current_assets', formik, idPrefix, inputDisabled)}
        {renderColumnSectionHeader('Activo corriente', columnTotals.current_assets ?? '0,00')}

        {/* Activo no corriente - conceptos luego total */}
        <Card margin={{ top: '3' }} />
        {renderColumnInput('Créditos por ventas LP', 'non_current_assets', 'trade_receivables_long_term', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Otros créditos', 'non_current_assets', 'other_receivables', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Inversiones permanentes', 'non_current_assets', 'permanent_investments', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Bienes de uso', 'non_current_assets', 'property_plant_and_equipment', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Otros activos no corrientes', 'non_current_assets', 'other_non_current_assets', formik, idPrefix, inputDisabled)}
        {renderColumnSectionHeader('Activo no corriente', columnTotals.non_current_assets ?? '0,00')}

        {/* Total Activo */}
        {renderColumnTotalRow('Total activo', columnTotals.total_current)}

        {/* Pasivo corriente - conceptos luego total */}
        {renderColumnInput('Deudas comerciales CP', 'current_liabilities', 'trade_payables_short_term', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Deuda bancaria y financiera CP', 'current_liabilities', 'bank_and_financial_debt_short_term', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Deudas cargas sociales CP', 'current_liabilities', 'social_security_debt_short_term', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Cargas fiscales CP', 'current_liabilities', 'tax_liabilities_short_term', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Otras deudas - provisiones', 'current_liabilities', 'other_debts_provisions', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Previsiones', 'current_liabilities', 'projections', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Otros pasivos', 'current_liabilities', 'other_liabilities', formik, idPrefix, inputDisabled)}
        {renderColumnSectionHeader('Pasivo corriente', columnTotals.current_liabilities ?? '0,00')}

        {/* Pasivo no corriente - conceptos luego total */}
        <Card margin={{ top: '3' }} />
        {renderColumnInput('Deudas comerciales LP', 'non_current_liabilities', 'trade_payables_long_term', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Deuda bancaria y financiera LP', 'non_current_liabilities', 'bank_and_financial_debt_long_term', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Cargas sociales LP', 'non_current_liabilities', 'social_security_debt_long_term', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Cargas fiscales LP', 'non_current_liabilities', 'tax_liabilities_long_term', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Otros pasivos LP', 'non_current_liabilities', 'other_long_term_liabilities', formik, idPrefix, inputDisabled)}
        {renderColumnSectionHeader('Pasivo no corriente', columnTotals.non_current_liabilities ?? '0,00')}

        {/* Total Pasivo */}
        {renderColumnTotalRow('Total pasivo', columnTotals.total_non_current)}

         {/* Patrimonio neto - conceptos luego total */}
         {renderColumnInput('Capital', 'net_worth', 'capital', formik, idPrefix, inputDisabled)}
         {isDetailedNetWorth && renderColumnInput('Reservas Legales', 'net_worth', 'legal_reserves', formik, idPrefix, inputDisabled)}
         {isDetailedNetWorth && renderColumnInput('Otras Reservas', 'net_worth', 'other_reserves', formik, idPrefix, inputDisabled)}
         {isDetailedNetWorth && renderColumnInput('Ajuste de Capital', 'net_worth', 'capital_adjustment', formik, idPrefix, inputDisabled)}
         {isDetailedNetWorth && renderColumnInput('Aportes Irrevocables', 'net_worth', 'irrevocable_contributions', formik, idPrefix, inputDisabled)}
         {isDetailedNetWorth && renderColumnInput('Revalúo Técnico', 'net_worth', 'technical_revaluation', formik, idPrefix, inputDisabled)}
         {isDetailedNetWorth && renderColumnInput('Acumuladas Ejercicio Anterior', 'net_worth', 'prior_accumulated', formik, idPrefix, inputDisabled)}
         {isDetailedNetWorth && renderColumnInput('Resultado del Ejercicio', 'net_worth', 'result_of_period', formik, idPrefix, inputDisabled)}
         {renderColumnInput('Otros', 'net_worth', 'other', formik, idPrefix, inputDisabled)}
         {!isDetailedNetWorth && (
           <Span variant='neutral' type='2'>
             Si el monto es negativo, agregar el signo &quot;-&quot;.
           </Span>
         )}
         {renderColumnSectionHeader('Patrimonio neto', columnTotals.net_worth ?? '0,00')}

        {/* Total Pasivo + PN */}
        {renderColumnTotalRow('Total pasivo + PN', columnTotals.total_net_worth_and_non_current)}
      </>
    );
  };

  // Render Estado de Resultados section for a column (conceptos primero, resultado debajo en cada bloque)
  const renderEstadoResultadosColumn = (
    formik: typeof balanceFormik,
    columnTotals: typeof totals,
    idPrefix: string,
    inputDisabled: boolean
  ) => {
    return (
      <>
        {/* Resultado bruto - conceptos luego total */}
        {renderColumnInput('Ventas', 'gross_result', 'sales', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Costo de ventas', 'gross_result', 'cost_of_sales', formik, idPrefix, inputDisabled)}
        {!isResultadoBrutoFiltered && (
          <>
            {renderColumnInput('Producción agropecuaria', 'gross_result', 'agricultural_production', formik, idPrefix, inputDisabled)}
            {renderColumnInput('Costo de producción', 'gross_result', 'production_cost', formik, idPrefix, inputDisabled)}
          </>
        )}
        {renderColumnSectionHeader('Resultado bruto', columnTotals.gross_result ?? '0,00')}

        {/* Resultado operativo */}
        <Card margin={{ top: '3' }} />
        {renderColumnInput('Gastos de comercialización', 'operating_result', 'commercialization_expenses', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Gastos de administración', 'operating_result', 'administrative_expenses', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Otros ingresos', 'operating_result', 'other_income', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Otros egresos', 'operating_result', 'other_expenses', formik, idPrefix, inputDisabled)}
        {renderColumnSectionHeader('Resultado operativo', columnTotals.operating_result ?? '0,00')}

        {/* Resultado ordinario */}
        <Card margin={{ top: '3' }} />
        {renderColumnInput('Gastos financieros', 'ordinary_result', 'financial_expenses', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Dif. de cambio + res. tenencia', 'ordinary_result', 'exchange_difference_and_holding_result', formik, idPrefix, inputDisabled)}
        {renderColumnSectionHeader('Resultado ordinario', columnTotals.ordinary_result ?? '0,00')}

        {/* Resultado antes de impuestos */}
        <Card margin={{ top: '3' }} />
        {renderColumnInput('Ingresos extraordinarios', 'result_before_taxes', 'extraordinary_income', formik, idPrefix, inputDisabled)}
        {renderColumnInput('Egresos extraordinarios', 'result_before_taxes', 'extraordinary_expenses', formik, idPrefix, inputDisabled)}
        {renderColumnSectionHeader('Resultado antes de impuestos', (columnTotals.result_before_taxes as { totalFormatted?: string })?.totalFormatted ?? '0,00')}

        {/* Impuestos + Resultado del ejercicio */}
        <Card margin={{ top: '3' }} />
        <Card display='flex' alignItems='center' gap='8px' margin={{ bottom: '2' }}>
          <div style={{ flex: 1 }}>
            <H type='4' variant='secondary-2' weight='bold'>Impuestos</H>
          </div>
          <div className="dual-balance-value-col" />
        </Card>
        {renderColumnInput('Impuestos a las ganancias', 'taxes', 'income_tax', formik, idPrefix, inputDisabled)}
        {renderColumnTotalRow('Resultado del ejercicio', columnTotals.taxes ?? '0,00', true)}
      </>
    );
  };

  const renderRejectModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Declinar una solicitud eliminará la documentación.
        </P>
        <P type='1' variant='danger' withMargin={false}>
          Esta acción no se puede deshacer
        </P>
        <P type='1' variant='neutral' withMargin={false}>
          También podés solicitar cambios. Detallalos abajo, y la solicitud
          volverá al productor con ese pedido.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  // Export button component
  const ExportButton = ({ disabled, onClick }: { disabled: boolean; onClick: () => void }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '52px',
        height: '52px',
        backgroundColor: !disabled ? '#1a1a1a' : '#e0e0e0',
        border: 'none',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: !disabled ? 'pointer' : 'not-allowed',
        padding: 0,
        marginLeft: '8px',
        marginTop: 'auto',
        flexShrink: 0
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke={!disabled ? 'white' : '#666'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M14 2V8H20" stroke={!disabled ? 'white' : '#666'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 12V18" stroke={!disabled ? 'white' : '#666'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 15L12 18L15 15" stroke={!disabled ? 'white' : '#666'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );

  return (
    <Card spacing={{ left: isUruguayEnabled ? '0' : '6' }}>
      <Toast id='balance-activity-toast' />
      <Toast id='application-toast' />
      {/* Balance load: input width, alignment, responsive and PDF export */}
      <style>{`
        .dual-balance-container {
          max-width: 100%;
          overflow-x: hidden;
        }
        .dual-balance-grid-col {
          min-width: 0;
        }
        .dual-balance-total-row {
          overflow: visible;
          min-width: 0;
        }
        .dual-balance-total-row .dual-balance-value-col {
          min-width: 0;
          max-width: none;
          flex-shrink: 1;
        }
        .dual-balance-total-row-title {
          min-width: 0;
          flex: 1 1 auto;
          overflow: visible;
        }
        .dual-balance-total-row-value {
          min-width: 0;
          flex: 0 1 auto;
          overflow: visible;
          white-space: nowrap;
        }
        .dual-balance-value-col {
          /* Make value column responsive and wider without hard caps */
          width: clamp(160px, 48%, 720px);
          max-width: 100%;
          min-width: 0;
          flex: 0 1 clamp(160px, 48%, 720px);
        }
        .dual-balance-input {
          /* Make inputs take more room (match mock) while staying responsive */
          width: clamp(160px, 48%, 720px);
          max-width: 100%;
          min-width: 0;
          flex: 0 1 clamp(160px, 48%, 720px);
        }
        .dual-balance-input input {
          text-align: right !important;
          width: 100% !important;
          box-sizing: border-box;
        }
        .dual-balance-complementary-input {
          width: clamp(160px, 48%, 720px);
          max-width: 100%;
          min-width: 0;
          flex: 0 1 clamp(160px, 48%, 720px);
        }
        .dual-balance-complementary-input input {
          text-align: right !important;
          width: 100% !important;
          box-sizing: border-box;
        }
        .dual-balance-total {
          display: flex !important;
          justify-content: flex-end !important;
          text-align: right !important;
        }
        .dual-balance-total * {
          text-align: right !important;
        }
        .dual-balance-grid {
          display: grid;
          grid-template-columns: 1fr 1px 1fr;
        }
        .dual-balance-divider {
          background-color: #E0E0E0;
        }
        .dual-balance-period-grid {
          display: grid;
          grid-template-columns: 1fr 1px 1fr;
          align-items: center;
          gap: 0;
        }
        .dual-balance-period-divider {
          background-color: #E0E0E0;
          align-self: stretch;
          min-height: 52px;
        }
        .dual-balance-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 16px;
        }
        @media (max-width: 768px) {
          .dual-balance-grid {
            grid-template-columns: 1fr !important;
          }
          .dual-balance-divider {
            display: none !important;
          }
          .dual-balance-period-grid {
            grid-template-columns: 1fr !important;
          }
          .dual-balance-period-divider {
            display: none !important;
          }
          .dual-balance-footer {
            flex-wrap: wrap;
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
          }
        }
        @media (max-width: 400px) {
          .dual-balance-value-col,
          .dual-balance-input,
          .dual-balance-complementary-input {
            min-width: 0;
            max-width: 100%;
          }
        }
        .pdf-export-mode .dual-balance-input > div > div,
        .pdf-export-mode .dual-balance-complementary-input > div > div {
          box-shadow: none !important;
          border: 1px solid #ccc !important;
          border-radius: 6px !important;
          padding-top: 14px !important;
          padding-bottom: 14px !important;
        }
      `}</style>
      <Modal
        isOpen={isRejectApplication}
        onClose={() => setIsRejectApplication(false)}
        title={'Declinar o solicitar cambios'}
        primaryText='Solicitar cambios'
        primaryVariant='primary'
        secondaryText='Declinar solicitud'
        secondaryVariant='secondary'
        onSecondaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.rejected.label);
        }}
        onPrimaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.pending.label);
        }}
        children={renderRejectModal()}
      />
      
      {/* Header - Sticky section with Visualization toggle and Period selectors */}
      <div style={{
        position: 'sticky',
        top: 0,
        backgroundColor: '#fff',
        zIndex: 10,
        paddingTop: '8px',
        paddingBottom: '16px',
        marginBottom: '0'
      }}>
        <Card display='flex' gap='16px' alignItems='center' margin={{ bottom: '3' }}>
          <P type='1' variant='neutral' withMargin={false} weight='bold'>
            Visualización
          </P>
          <Card display='flex' gap='16px' alignItems='center'>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type='radio'
                name='visualization-mode'
                checked={isEditableMode}
                onChange={() => setIsEditableMode(true)}
                style={{ accentColor: '#333333' }}
              />
              <Span type='2' variant='neutral'>Editable</Span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type='radio'
                name='visualization-mode'
                checked={!isEditableMode}
                onChange={() => setIsEditableMode(false)}
                style={{ accentColor: '#333333' }}
              />
              <Span type='2' variant='neutral'>Solo lectura</Span>
            </label>
          </Card>
        </Card>
        
        {/* Period selectors using CSS Grid for proper alignment with dividers below */}
        <div className="dual-balance-period-grid">
          {/* Current period column (left side) */}
          <div style={{ display: 'flex', alignItems: 'center', paddingRight: '24px' }}>
            <div style={{ minWidth: '160px', marginRight: '16px' }}>
              <P type='2' variant='neutral' withMargin={false}>
                Período del balance
              </P>
            </div>
            <div style={{ flex: 1 }}>
              <DropDown
                id='balance-curr-month-dropdown'
                items={values.map((value) => ({ value: value, label: value }))}
                title={correspondingValue || 'Seleccionar período'}
                onChange={(e) => {
                  const index = values.indexOf(e);
                  if (index !== -1) {
                    setSelectedKey(keys[index]);
                    setCorrespondingValue(e);
                    const [fromMonth, fromYear] = keys[index].split('-');
                    const [toMonth, toYear] = e.split('-');
                    balanceFormik.setFieldValue('from_month', MONTH_NAMES.indexOf(fromMonth) + 1);
                    balanceFormik.setFieldValue('from_year', parseInt(fromYear));
                    balanceFormik.setFieldValue('to_month', MONTH_NAMES.indexOf(toMonth) + 1);
                    balanceFormik.setFieldValue('to_year', parseInt(toYear));
                  }
                }}
                disabled={!isApplicationEditable() || !isEditableMode}
              />
            </div>
          </div>

          {/* Vertical divider */}
          <div className="dual-balance-period-divider" />

          {/* Previous period column (right side) with download button */}
          <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '24px' }}>
            <div style={{ flex: 1 }}>
              <DropDown
                id='balance-prev-month-dropdown'
                items={values.map((value) => ({ value: value, label: value }))}
                title={previousCorrespondingValue || 'Seleccionar período'}
                onChange={(e) => {
                  const index = values.indexOf(e);
                  if (index !== -1) {
                    setPreviousSelectedKey(keys[index]);
                    setPreviousCorrespondingValue(e);
                    const [fromMonth, fromYear] = keys[index].split('-');
                    const [toMonth, toYear] = e.split('-');
                    previousBalanceFormik.setFieldValue('from_month', MONTH_NAMES.indexOf(fromMonth) + 1);
                    previousBalanceFormik.setFieldValue('from_year', parseInt(fromYear));
                    previousBalanceFormik.setFieldValue('to_month', MONTH_NAMES.indexOf(toMonth) + 1);
                    previousBalanceFormik.setFieldValue('to_year', parseInt(toYear));
                  }
                }}
                disabled={!isApplicationEditable() || !isEditableMode}
              />
            </div>
            {/* Download button at the end (right) */}
            <ExportButton 
              disabled={!selectedKey || isPdfGenerating} 
              onClick={() => generatePdfFromNode(balanceContainerRef.current)} 
            />
          </div>
        </div>
      </div>
      
      {/* Dual Balance Container - wrapped with ref for PDF export */}
      <div ref={balanceContainerRef} className="dual-balance-container">
      <Card margin={{ top: '4' }}>
        {/* Horizontal divider aligned with vertical divider */}
        <div style={{ 
          width: '100%', 
          height: '1px', 
          backgroundColor: '#E0E0E0',
          marginBottom: '16px'
        }} />
        
        {/* Estado patrimonial - Common title */}
        <Card margin={{ bottom: '3' }}>
          <H type='3' variant='secondary-2' weight='bold'>
            Estado patrimonial
          </H>
        </Card>
        
        {/* Two columns for Estado Patrimonial using CSS Grid */}
        <div className="dual-balance-grid" style={{ minHeight: '500px' }}>
          {/* Current Balance Column (left) */}
          <div className="dual-balance-grid-col" style={{ paddingRight: '24px' }}>
            {renderEstadoPatrimonialColumn(
              balanceFormik,
              totals,
              'curr',
              !selectedKey
            )}
          </div>
          
          {/* Vertical divider */}
          <div className="dual-balance-divider" />
          
          {/* Previous Balance Column (right) */}
          <div className="dual-balance-grid-col" style={{ paddingLeft: '24px' }}>
            {renderEstadoPatrimonialColumn(
              previousBalanceFormik,
              previousTotals,
              'prev',
              !previousSelectedKey
            )}
          </div>
        </div>

        {/* Estado de Resultados - Common title */}
        <Card margin={{ bottom: '3', top: '4' }}>
          <H type='3' variant='secondary-2' weight='bold'>
            Estado de Resultados
          </H>
        </Card>
        
        {/* Horizontal divider for Estado de Resultados */}
        <div style={{ 
          width: '100%', 
          height: '1px', 
          backgroundColor: '#E0E0E0',
          marginBottom: '16px'
        }} />
        
        {/* Two columns for Estado de Resultados using CSS Grid */}
        <div className="dual-balance-grid" style={{ minHeight: '400px' }}>
          {/* Current Balance Column (left) */}
          <div className="dual-balance-grid-col" style={{ paddingRight: '24px' }}>
            {renderEstadoResultadosColumn(
              balanceFormik,
              totals,
              'curr',
              !selectedKey
            )}
          </div>
          
          {/* Vertical divider */}
          <div className="dual-balance-divider" />
          
          {/* Previous Balance Column (right) */}
          <div className="dual-balance-grid-col" style={{ paddingLeft: '24px' }}>
            {renderEstadoResultadosColumn(
              previousBalanceFormik,
              previousTotals,
              'prev',
              !previousSelectedKey
            )}
          </div>
        </div>

        {isComplementaryDataEnabled && (
          <>
            <Card margin={{ bottom: '3', top: '4' }}>
              <H type='3' variant='secondary-2' weight='bold'>
                Datos Complementarios
              </H>
            </Card>
            <div style={{ width: '100%', height: '1px', background: '#e0e0e0', marginBottom: '16px' }} />
            <div className="dual-balance-grid">
              <div className="dual-balance-grid-col" style={{ paddingRight: '24px' }}>
                <Card display='flex' alignItems='center' gap='12px' margin={{ bottom: '2' }}>
                  <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <Span type='2' variant='neutral'>Compras</Span>
                  </div>
                  <div className="dual-balance-complementary-input">
                    <NumericFormat
                      disabled={!selectedKey || !isApplicationEditable() || !isEditableMode}
                      id='risk-balance-curr-input-purchases'
                      maxLength={40}
                      placeholder='Ingresar monto'
                      value={balanceFormik.values.purchases ?? ''}
                      thousandSeparator='.'
                      decimalSeparator=','
                      onValueChange={(formatValues) =>
                        balanceFormik.setFieldValue('purchases', formatValues.value === '' ? '' : formatValues.floatValue)
                      }
                      customInput={Input}
                      decimalScale={2}
                      allowNegative={true}
                    />
                  </div>
                </Card>
                <Card display='flex' alignItems='center' gap='12px' margin={{ bottom: '2' }}>
                  <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <Span type='2' variant='neutral'>Amortizaciones y Depreciaciones</Span>
                  </div>
                  <div className="dual-balance-complementary-input">
                    <NumericFormat
                      disabled={!selectedKey || !isApplicationEditable() || !isEditableMode}
                      id='risk-balance-curr-input-amortization-depreciation'
                      maxLength={40}
                      placeholder='Ingresar monto'
                      value={balanceFormik.values.amortization_depreciation ?? ''}
                      thousandSeparator='.'
                      decimalSeparator=','
                      onValueChange={(formatValues) =>
                        balanceFormik.setFieldValue('amortization_depreciation', formatValues.value === '' ? '' : formatValues.floatValue)
                      }
                      customInput={Input}
                      decimalScale={2}
                      allowNegative={true}
                    />
                  </div>
                </Card>
              </div>

              <div className="dual-balance-divider" />

              <div className="dual-balance-grid-col" style={{ paddingLeft: '24px' }}>
                <Card display='flex' alignItems='center' gap='12px' margin={{ bottom: '2' }}>
                  <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <Span type='2' variant='neutral'>Compras</Span>
                  </div>
                  <div className="dual-balance-complementary-input">
                    <NumericFormat
                      disabled={!previousSelectedKey || !isApplicationEditable() || !isEditableMode}
                      id='risk-balance-prev-input-purchases'
                      maxLength={40}
                      placeholder='Ingresar monto'
                      value={previousBalanceFormik.values.purchases ?? ''}
                      thousandSeparator='.'
                      decimalSeparator=','
                      onValueChange={(formatValues) =>
                        previousBalanceFormik.setFieldValue('purchases', formatValues.value === '' ? '' : formatValues.floatValue)
                      }
                      customInput={Input}
                      decimalScale={2}
                      allowNegative={true}
                    />
                  </div>
                </Card>
                <Card display='flex' alignItems='center' gap='12px' margin={{ bottom: '2' }}>
                  <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <Span type='2' variant='neutral'>Amortizaciones y Depreciaciones</Span>
                  </div>
                  <div className="dual-balance-complementary-input">
                    <NumericFormat
                      disabled={!previousSelectedKey || !isApplicationEditable() || !isEditableMode}
                      id='risk-balance-prev-input-amortization-depreciation'
                      maxLength={40}
                      placeholder='Ingresar monto'
                      value={previousBalanceFormik.values.amortization_depreciation ?? ''}
                      thousandSeparator='.'
                      decimalSeparator=','
                      onValueChange={(formatValues) =>
                        previousBalanceFormik.setFieldValue('amortization_depreciation', formatValues.value === '' ? '' : formatValues.floatValue)
                      }
                      customInput={Input}
                      decimalScale={2}
                      allowNegative={true}
                    />
                  </div>
                </Card>
              </div>
            </div>
          </>
        )}
      </Card>
      </div>

      {/* Action buttons - two direct children so they stack on small screens */}
      <div className="dual-balance-footer">
        <div>
          {canTransitionRisk && !isSaving && isApplicationEditable() && (
            <Button
              variant='secondary'
              onClick={() => setIsRejectApplication(true)}
              spacing={{ left: '4', right: '4' }}
            >
              Declinar o solicitar cambios
            </Button>
          )}
        </div>
        <Card display='flex' gap='8px' alignItems='center'>
          {isSaving && <Spinner />}
          {!isSaving && isApplicationEditable() && (
            <>
              <Button
                variant='secondary'
                onClick={() => saveData(false)}
                disabled={isDisabled || !selectedKey}
                spacing={{ left: '4', right: '4' }}
              >
                Guardar
              </Button>
              <Button
                variant='primary'
                onClick={() => saveData(true)}
                disabled={isDisabled || !selectedKey}
                spacing={{ left: '4', right: '4' }}
              >
                Continuar
              </Button>
            </>
          )}
          {!isSaving && !isApplicationEditable() && (
            <Button
              variant='primary'
              onClick={() => saveData(true)}
              spacing={{ left: '4', right: '4' }}
            >
              Continuar
            </Button>
          )}
        </Card>
      </div>
    </Card>
  );
};

export default DualBalanceLoader;
